import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { getAuthHeaders } from '../utils/authHelpers';

const API_ROOT_URL = process.env.REACT_APP_API_ROOT_URL;
const ORDERS_API_URL = `${API_ROOT_URL}/orders`;

export function useDashboardData() {
  const { user } = useAuth();
  const [allOrders, setAllOrders] = useState([]); // Stores the full, unfiltered list
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submissionMessage, setSubmissionMessage] = useState({ message: '', type: '' });
  const [orderTaskStates, setOrderTaskStates] = useState({});
  const [availableOrderTypes, setAvailableOrderTypes] = useState(['All']);

  const allOrderStages = useMemo(
    () => ['All', 'Administration', 'Pre-Production', 'Manufacturing', 'Shipping', 'Completed'],
    []
  );

  const stageMap = useMemo(() => ({
    'Administration': 'administration',
    'Pre-Production': 'preProduction',
    'Manufacturing': 'manufacturing',
    'Shipping': 'shipping',
    'Completed': 'completed',
  }), []);

  // Tasks (memoized)
  const ADMIN_TASKS = useMemo(() => [
    { id: 'checkOrderAndLog', label: 'Check order & log for discrepancies', type: 'checkbox'},
    { id: 'orderInvoiceCreated', label: 'Order invoice created **', type: 'checkbox' },
    { id: 'invoiceEmailSent', label: 'Invoice sent via email: Please review and approve', type: 'checkbox' },
    { id: 'invoiceApproved', label: 'Dealer approved Invoice: Waiting for payment processing', type: 'checkbox' },
    { id: 'paymentCompletedDate', label: 'Payment completed', type: 'date', required: false },
  ], []);

  const PRE_PRODUCTION_TASKS = useMemo(() => [
    { id: 'invoiceAndPackingSlipsReview', label: 'Invoice and packing slips review completed', type: 'checkbox' },
    { id: 'fastPassReview', label: 'FastPass review completed', type: 'checkbox' },
    { id: 'orderPacketsAndLabelsProcessed', label: 'Order packets and labels processed', type: 'checkbox' },
    { id: 'sentToManufacturing', label: 'Order sent to manufacturing department', type: 'checkbox' },
  ], []);

  const MANUFACTURING_TASKS = useMemo(() => [
    // 1. Manager Tasks (Scheduling)
    { id: 'orderEnteredProductionDate', label: 'Order Entered Production', type: 'date', required: true, editableBy: [] },

    // 2. Welder Tasks
    { id: 'bracketsCompleted', label: 'Brackets completed', type: 'checkbox', editableBy: ['Manufacturing', 'Supervisor'] },

    // 3. Assembler Tasks (Frame & Components)
    { id: 'upperFrameCompleted', label: 'Upper Frame completed', type: 'checkbox', editableBy: ['Manufacturing', 'Supervisor'] },
    { id: 'lowerFrameCompleted', label: 'Lower Frame completed', type: 'checkbox', editableBy: ['Manufacturing', 'Supervisor'] },
    { id: 'midFrameCompleted', label: 'Mid Frame completed', type: 'checkbox', editableBy: ['Manufacturing', 'Supervisor'] },
    { id: 'motorCompleted', label: 'Motor completed', type: 'checkbox', editableBy: ['Manufacturing', 'Supervisor'] },
    { id: 'fiberglassRodsCompleted', label: 'Fiberglass rods completed', type: 'checkbox', editableBy: ['Manufacturing', 'Supervisor'] },
    { id: 'ridgeSupportsCompleted', label: 'Ridge Supports completed', type: 'checkbox', editableBy: ['Manufacturing', 'Supervisor'] },
    { id: 'hardwareKitCompleted', label: 'Hardware Kit completed', type: 'checkbox', editableBy: ['Manufacturing', 'Supervisor'] },

    // 4. Seamstress Tasks (Drawing)
    { id: 'upperAndLowerFabricsDrawingsCompleted', label: 'Fabrics Drawings Completed', type: 'checkbox', editableBy: ['Supervisor', 'Employee'] },
    { id: 'extraComponentsDrawingsCompleted', label: 'Extra Components Drawings Completed', type: 'checkbox', editableBy: ['Supervisor', 'Employee'] },

    // 5. Seamstress Tasks (Cutting)
    { id: 'upperAndLowerFabricsCut', label: 'Upper & Lower Fabrics Cut', type: 'checkbox', editableBy: ['Employee', 'Supervisor'] },
    { id: 'cableLinersCut', label: 'Cable Liners Cut', type: 'checkbox', editableBy: ['Employee', 'Supervisor'] },
    { id: 'chafePanelsCut', label: 'Chafe Panels Cut', type: 'checkbox', editableBy: ['Employee', 'Supervisor'] },
    { id: 'pileCoversCut', label: 'Pile Covers Cut', type: 'checkbox', editableBy: ['Employee', 'Supervisor'] },
    { id: 'fabricSkirtsCut', label: 'Fabric Skirts Cut', type: 'checkbox', editableBy: ['Employee', 'Supervisor'] },
    { id: 'birdPanelsCut', label: 'Bird Panels Cut', type: 'checkbox', editableBy: ['Employee', 'Supervisor'] },
    { id: 'dropPanelsCut', label: 'Drop Panels Cut', type: 'checkbox', editableBy: ['Employee', 'Supervisor'] },

    // 6. Seamstress Tasks (Sewing)
    { id: 'upperFabricToFit', label: 'Upper Fabric to Fit', type: 'checkbox', editableBy: ['Employee', 'Supervisor'] },
    { id: 'upperFabricInProgress', label: 'Upper Fabric In Progress', type: 'checkbox', editableBy: ['Employee', 'Supervisor'] },
    { id: 'lowerFabricInProgress', label: 'Lower Fabric In Progress', type: 'checkbox', editableBy: ['Employee', 'Supervisor'] },
    { id: 'chafePanelsInProgress', label: 'Chafe Panels In Progress', type: 'checkbox', editableBy: ['Employee', 'Supervisor'] },
    { id: 'pileCoversInProgress', label: 'Pile Covers In Progress', type: 'checkbox', editableBy: ['Employee', 'Supervisor'] },
    { id: 'fabricSkirtsInProgress', label: 'Fabric Skirts In Progress', type: 'checkbox', editableBy: ['Employee', 'Supervisor'] },
    { id: 'birdPanelsInProgress', label: 'Bird Panels In Progress', type: 'checkbox', editableBy: ['Employee', 'Supervisor'] },
    { id: 'dropPanelsInProgress', label: 'Drop Panels In Progress', type: 'checkbox', editableBy: ['Employee', 'Supervisor'] },

    // 7. Seamstress Tasks (Completion/Packing)
    { id: 'upperFabricCompleted', label: 'Upper Fabric Completed', type: 'checkbox', editableBy: ['Manufacturing', 'Supervisor'] },
    { id: 'lowerFabricCompleted', label: 'Lower Fabric Completed', type: 'checkbox', editableBy: ['Manufacturing', 'Supervisor'] },
    { id: 'chafePanelsCompleted', label: 'Chafe Panels Completed', type: 'checkbox', editableBy: ['Manufacturing', 'Supervisor'] },
    { id: 'pileCoversCompleted', label: 'Pile Covers Completed', type: 'checkbox', editableBy: ['Manufacturing', 'Supervisor'] },
    { id: 'fabricSkirtsCompleted', label: 'Fabric Skirts Completed', type: 'checkbox', editableBy: ['Manufacturing', 'Supervisor'] },
    { id: 'birdPanelsCompleted', label: 'Bird Panels Completed', type: 'checkbox', editableBy: ['Manufacturing', 'Supervisor'] },
    { id: 'dropPanelsCompleted', label: 'Drop Panels Completed', type: 'checkbox', editableBy: ['Manufacturing', 'Supervisor'] },
    { id: 'upperFabricBoxedUp', label: 'Upper Fabric Boxed Up', type: 'checkbox', editableBy: ['Manufacturing', 'Employee', 'Supervisor'] },
    { id: 'lowerFabricAndCableLinersBoxedUp', label: 'Lower Fabric and Cable Liners Boxed Up', type: 'checkbox', editableBy: ['Manufacturing', 'Employee', 'Supervisor'] },
    
  ], []);

  const SHIPPING_TASKS = useMemo(() => [
    { id: 'completionCheck', label: 'Ensured completion of the order', type: 'checkbox', required: false, editableBy: ['Shipping'] },
    { id: 'palletSecured', label: 'Pallet secured with straps', type: 'checkbox', required: false, editableBy: ['Shipping'] },
    { id: 'palletWrapped', label: 'Pallet wrapped', type: 'checkbox', required: false, editableBy: ['Shipping'] },
    { id: 'shippedDate', label: 'Shipping Date', type: 'date', required: true, editableBy: ['Shipping'] },
  ], []);

  const getTasksForStage = useCallback((stage) => {
  let tasks = [];

  switch (stage) {
    case 'Administration': 
      tasks = ADMIN_TASKS; 
      break;
    case 'Pre-Production': 
      tasks = PRE_PRODUCTION_TASKS; 
      break;
    case 'Manufacturing': 
      tasks = MANUFACTURING_TASKS; 
      break;
    case 'Shipping': 
      tasks = SHIPPING_TASKS; 
      break;
    default: 
      return [];
  }

  // Visibility filtering
  return tasks.filter(task => {
    // If no visibleTo defined → everyone can see it
    if (!task.visibleTo) return true;

    return task.visibleTo.includes(user?.role);
  });

}, [ADMIN_TASKS, PRE_PRODUCTION_TASKS, MANUFACTURING_TASKS, SHIPPING_TASKS, user]);


  const formatDate = useCallback((dateLike, includeTime = true) => {
    if (!dateLike) return 'N/A';

    const isDateOnly =
      typeof dateLike === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateLike);

    let d;
    if (isDateOnly) {
      const [y, m, day] = dateLike.split('-').map(Number);
      d = new Date(Date.UTC(y, m - 1, day));
    } else {
      d = new Date(dateLike);
    }
    if (Number.isNaN(d.getTime())) return 'N/A';

    const options = includeTime
      ? { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'UTC' }
      : { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' };

    return d.toLocaleDateString(undefined, options);
  }, []);

  const getStageBadgeVariant = useCallback((stage) => {
    switch (stage) {
      case 'Administration': return 'info';
      case 'Pre-Production': return 'primary';
      case 'Manufacturing': return 'warning';
      case 'Shipping': return 'success';
      case 'Completed': return 'dark';
      default: return 'secondary';
    }
  }, []);

  const fetchOrdersForRole = useCallback(async () => {
  setLoading(true);
  setError('');
  setSubmissionMessage({ message: '', type: '' });

  if (!user || !user.role) {
    setAllOrders([]);
    setOrderTaskStates({});
    setLoading(false);
    return;
  }

  try {
    const res = await fetch(ORDERS_API_URL, {
      method: 'GET',
      headers: {
        ...getAuthHeaders()
      }
    });

    if (!res.ok) throw new Error(`HTTP error: ${res.status}`);
    const allFetchedOrders = await res.json();

    let roleFiltered = [];

    switch (user.role) {
      case 'Admin':
        roleFiltered = allFetchedOrders.filter(
          o => o.currentStage === 'Administration'
        );
        break;

      case 'PreProduction':
        roleFiltered = allFetchedOrders.filter(o => o.currentStage === 'Pre-Production');
        break;

      case 'Manufacturing':
        roleFiltered = allFetchedOrders.filter(o => 
          ['Manufacturing', 'Shipping', 'Completed'].includes(o.currentStage)
        );
        break;

      case 'Supervisor':
      case 'Employee':
        roleFiltered = allFetchedOrders.filter(o => o.currentStage === 'Manufacturing');
        break;

      case 'Shipping':
        roleFiltered = allFetchedOrders.filter(o => o.currentStage === 'Shipping');
        break;

      case 'Dealer':
        roleFiltered = allFetchedOrders.filter(o => o.dealerId === user.associatedDealerId);
        break;

      default:
        roleFiltered = [];
    }

    // Non-mutating sort to produce a new array reference
    const sortedOrders = [...roleFiltered].sort((a, b) => {
      const dateA = new Date(a.orderDate);
      const dateB = new Date(b.orderDate);
      return dateB.getTime() - dateA.getTime();
    });

    // Set state with a new array reference to trigger re-render
    setAllOrders(sortedOrders.map(order => ({ ...order })));

  } catch (err) {
    console.error(err);
    setError('Failed to load orders.');
  } finally {
    setLoading(false);
  }
}, [user]);


  useEffect(() => {
    fetchOrdersForRole();
  }, [fetchOrdersForRole]);

  useEffect(() => {
    if (allOrders && allOrders.length > 0) {
        const types = new Set(allOrders.map(order => order.orderType));
        setAvailableOrderTypes(['All', ...Array.from(types)]);
    } else {
        setAvailableOrderTypes(['All']);
    }
  }, [allOrders]);

  // Add this helper function inside useDashboardData, above the return statement
  const groupTasksForDisplay = useCallback((tasks) => {
      if (!tasks || tasks.length === 0) return [];

      const grouped = {
          'Manager Tasks': { group: 'Manager Tasks', tasks: [] },
          'Welder Tasks': { group: 'Welder Tasks', tasks: [] },
          'Assembler Tasks': { group: 'Assembler Tasks', tasks: [] },
          'Seamstress Tasks': { group: 'Seamstress Tasks', tasks: [] }
      };

      // Helper to find the group name based on task ID prefixes
      const getGroupName = (taskId) => {
          if (taskId.includes('orderEnteredProductionDate') || taskId.includes('orderReceived') || taskId.includes('orderScheduledForProductionDate')) return 'Manager Tasks';
          if (taskId.includes('bracketsCompleted')) return 'Welder Tasks';
          if (taskId.includes('FrameCompleted') || taskId.includes('motorCompleted') || taskId.includes('RodsCompleted') || taskId.includes('SupportsCompleted') || taskId.includes('hardwareKitCompleted')) return 'Assembler Tasks';
          // Seamstress tasks are grouped further (Drawing, Cutting, Packing)
          return 'Seamstress Tasks';
      };

      // Populate groups
      tasks.forEach(task => {
          const groupName = getGroupName(task.id);
          if (grouped[groupName]) {
              // Seamstress tasks need sub-grouping
              if (groupName === 'Seamstress Tasks') {
                  let subGroup;
                  if (task.id.includes('DrawingsCompleted')) {
                      subGroup = 'Drawing';
                  } else if (task.id.includes('Cut')) {
                      subGroup = 'Cutting';
                  } else if (task.id.includes('Progress') || task.id.includes('ToFit')) {
                      subGroup = 'Sewing';
                  } else {
                      subGroup = 'Completing & Packing';
                  }
                  
                  // Ensure subtasks array exists
                  if (!grouped[groupName].subtasks) {
                      grouped[groupName].subtasks = {};
                  }
                  if (!grouped[groupName].subtasks[subGroup]) {
                      grouped[groupName].subtasks[subGroup] = { subgroup: subGroup, tasks: [] };
                  }
                  
                  grouped[groupName].subtasks[subGroup].tasks.push(task);
              } else {
                  grouped[groupName].tasks.push(task);
              }
          }
      });

      // Convert the dictionary back to a clean array, flatten subtasks for iteration
      const result = Object.values(grouped).filter(g => g.tasks.length > 0 || (g.subtasks && Object.keys(g.subtasks).length > 0));

      // Special handling for Seamstress tasks: convert subtasks dict to array
      const seamstressGroup = result.find(g => g.group === 'Seamstress Tasks');
      if (seamstressGroup && seamstressGroup.subtasks) {
          seamstressGroup.subtasks = Object.values(seamstressGroup.subtasks);
      }
      
      return result;
  }, []);

  // Expose the raw data and functions, but not the states used for filtering.
  // The filtering logic and filtered orders will now be handled inside the DashboardPage.js
  return {
    user,
    allOrders,
    loading,
    error,
    submissionMessage,
    setSubmissionMessage,
    orderTaskStates,
    setOrderTaskStates,
    allOrderStages,
    availableOrderTypes,
    stageMap,
    getTasksForStage,
    formatDate,
    getStageBadgeVariant,
    fetchOrdersForRole,
    groupTasksForDisplay
  };
}
